import type { HmiCanvasDocument, HmiElement } from "../schema/hmi-canvas.schema";
import type { HmiBinding } from "../schema/hmi-bindings.schema";
import { createDefaultBinding, createDefaultElement } from "../utils/default-document";

export type HmiViewport = {
  x: number;
  y: number;
  zoom: number;
};

function clampZoom(zoom: number): number {
  return Math.min(2.5, Math.max(0.4, zoom));
}

function referencedElementAssetIds(elements: HmiElement[]): Set<string> {
  return new Set(
    elements
      .filter((element) => element.type === "svg_asset" || element.type === "image_asset")
      .map((element) => element.asset.assetId)
      .filter((assetId): assetId is string => typeof assetId === "string" && assetId.length > 0),
  );
}

function pruneUnreferencedImageAssets(document: HmiCanvasDocument, elements: HmiElement[]): HmiCanvasDocument["assets"] {
  const referencedAssetIds = referencedElementAssetIds(elements);
  return document.assets.filter((asset) => asset.kind !== "image" || referencedAssetIds.has(asset.id));
}

export function addElementToDocument(document: HmiCanvasDocument, type: HmiElement["type"]): HmiCanvasDocument {
  return addConfiguredElementToDocument(document, type);
}

export function addConfiguredElementToDocument(
  document: HmiCanvasDocument,
  type: HmiElement["type"],
  options?: { symbolDefinitionId?: string },
): HmiCanvasDocument {
  const nextZ = document.elements.reduce((max, element) => Math.max(max, element.zIndex), 0) + 1;
  const element = createDefaultElement(type, nextZ, options);
  const binding = createDefaultBinding(element);
  return {
    ...document,
    elements: [...document.elements, element],
    bindings: binding ? [...document.bindings, binding] : document.bindings,
    metadata: {
      ...document.metadata,
      updatedAt: new Date().toISOString(),
    },
  };
}

export function duplicateElementInDocument(document: HmiCanvasDocument, elementId: string): HmiCanvasDocument {
  const existing = document.elements.find((element) => element.id === elementId);
  if (!existing) {
    return document;
  }
  const nextZ = document.elements.reduce((max, element) => Math.max(max, element.zIndex), 0) + 1;
  const copy = {
    ...existing,
    id: `${existing.id}_copy_${Math.random().toString(36).slice(2, 7)}`,
    x: existing.x + 24,
    y: existing.y + 24,
    zIndex: nextZ,
    locked: false,
  };
  const nextBindings = document.bindings
    .filter((binding) => binding.elementId === elementId)
    .map((binding) => ({
      ...binding,
      id: `${binding.id}_copy_${Math.random().toString(36).slice(2, 7)}`,
      elementId: copy.id,
    }));
  return {
    ...document,
    elements: [...document.elements, copy],
    bindings: nextBindings.length ? [...document.bindings, ...nextBindings] : document.bindings,
    metadata: {
      ...document.metadata,
      updatedAt: new Date().toISOString(),
    },
  };
}

export function reorderElementInDocument(
  document: HmiCanvasDocument,
  elementId: string,
  direction: "forward" | "backward" | "front" | "back",
): HmiCanvasDocument {
  const ordered = document.elements.slice().sort((a, b) => a.zIndex - b.zIndex);
  const index = ordered.findIndex((element) => element.id === elementId);
  if (index < 0) {
    return document;
  }
  const next = ordered.slice();
  const [target] = next.splice(index, 1);
  if (!target) {
    return document;
  }
  let nextIndex = index;
  if (direction === "forward") nextIndex = Math.min(next.length, index + 1);
  if (direction === "backward") nextIndex = Math.max(0, index - 1);
  if (direction === "front") nextIndex = next.length;
  if (direction === "back") nextIndex = 0;
  next.splice(nextIndex, 0, target);
  const normalized = next.map((element, position) => ({ ...element, zIndex: position + 1 }));
  return {
    ...document,
    elements: normalized,
    metadata: {
      ...document.metadata,
      updatedAt: new Date().toISOString(),
    },
  };
}

export function updateElementInDocument(
  document: HmiCanvasDocument,
  elementId: string,
  updater: (element: HmiElement) => HmiElement,
): HmiCanvasDocument {
  const nextElements = document.elements.map((element) => (element.id === elementId ? updater(element) : element));
  return {
    ...document,
    elements: nextElements,
    assets: pruneUnreferencedImageAssets(document, nextElements),
    metadata: {
      ...document.metadata,
      updatedAt: new Date().toISOString(),
    },
  };
}

export function removeElementFromDocument(document: HmiCanvasDocument, elementId: string): HmiCanvasDocument {
  const nextElements = document.elements.filter((element) => element.id !== elementId);
  return {
    ...document,
    elements: nextElements,
    bindings: document.bindings.filter((binding) => binding.elementId !== elementId),
    assets: pruneUnreferencedImageAssets(document, nextElements),
    metadata: {
      ...document.metadata,
      updatedAt: new Date().toISOString(),
    },
  };
}

export function updateBindingsInDocument(
  document: HmiCanvasDocument,
  elementId: string,
  updater: (bindings: HmiBinding[]) => HmiBinding[],
): HmiCanvasDocument {
  const current = document.bindings.filter((binding) => binding.elementId === elementId);
  const next = updater(current);
  const remaining = document.bindings.filter((binding) => binding.elementId !== elementId);
  return {
    ...document,
    bindings: [...remaining, ...next],
    metadata: {
      ...document.metadata,
      updatedAt: new Date().toISOString(),
    },
  };
}

export function nudgeViewport(viewport: HmiViewport, delta: Partial<HmiViewport>): HmiViewport {
  return {
    x: delta.x ?? viewport.x,
    y: delta.y ?? viewport.y,
    zoom: clampZoom(delta.zoom ?? viewport.zoom),
  };
}

export function zoomViewportAroundPoint(viewport: HmiViewport, factor: number, point: { x: number; y: number }): HmiViewport {
  const nextZoom = clampZoom(viewport.zoom * factor);
  const scale = nextZoom / viewport.zoom;
  return {
    zoom: nextZoom,
    x: point.x - (point.x - viewport.x) * scale,
    y: point.y - (point.y - viewport.y) * scale,
  };
}
